/**
 * Shared analytics-beacon startup for BOTH widget entry points (#1367).
 *
 * The widget has two entries and they must behave identically:
 *   - `bootstrap.jsx init()` — the `data-shiftgears` embed path (client sites).
 *   - `main.jsx`            — the standalone shell (demo.shiftgears.ai, incl.
 *                             the QR landing route `#/cars/:id/landing`).
 *
 * The beacon used to be started only from the first one, so everything served
 * from the standalone shell emitted zero `analytics` events and QR scans were
 * structurally uncountable. Keeping the startup in one module is what stops the
 * two entries drifting apart again — the same reason `themeApply` and
 * `headerHeight` live here rather than being copied into each entry.
 *
 * @param {Object} opts
 * @param {string} opts.apiUrl        Base API URL; beacon strips a trailing /api itself.
 * @param {string} [opts.frontendToken] Frontend access token; beacon self-disables without it.
 */
import { initBeacon, trackPageView } from '../services/analyticsBeacon';
import { getOrCreateVisitorId, getCurrentSessionId } from '../services/sessionManager';
import { captureAttribution } from '../services/attributionManager';

export function startBeacon({ apiUrl, frontendToken }) {
  try {
    // Marketing attribution (#1467) — BEFORE the page view, and from here rather
    // than either entry file, for the same reason the beacon itself lives here:
    // the ad parameters exist only on the landing URL, so both entries have to
    // read them or attribution silently depends on which path the host used.
    // Never allowed to break beacon startup — it is a nice-to-have on top.
    try {
      captureAttribution();
    } catch (e) {
      console.warn('[ShiftGears] Attribution capture failed:', e);
    }

    initBeacon({
      apiUrl,
      frontendToken: frontendToken || '',
      visitorId: getOrCreateVisitorId(),
      sessionId: getCurrentSessionId(),
    });
    trackPageView();
  } catch (e) {
    console.warn('[ShiftGears] Beacon init failed:', e);
  }
}
